import { Component, computed, signal, OnDestroy } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { AuthService } from '../../../core/service/auth/auth.service';
import { PermissionService } from '../../../core/service/permission/permission.service';
import { ApiService } from '../../../core/service/api-service/api-service';
import { filter, takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';

interface SportOption { sport_id: number; sport_name: string; }
interface CompetitionTypeOption { codification_code: string; codification_label: string; }

/** Nav routes that fan out into one item per sport once the federation's sports have loaded,
 *  then one sub-item per competition type under each sport. */
const SPORT_SCOPED_ROUTES = new Set(['/federation/competitions']);

interface NavItem {
  label: string;
  route: string;
  icon: string;
  permission?: { project: string; module: string; action: string };
  children?: NavItem[];
}

const NAV_BY_ROLE: Record<string, NavItem[]> = {
  FED_ADMIN: [
    { label: 'Tableau de bord',  route: '/federation/dashboard',    icon: 'dashboard' },
    { label: 'Compétitions',     route: '/federation/competitions',  icon: 'emoji_events' },
  ],
  SUPER_ADMIN: [
    { label: 'Tableau de bord',  route: '/federation/dashboard',    icon: 'dashboard' },
    { label: 'Compétitions',     route: '/federation/competitions',  icon: 'emoji_events' },
  ],
  FED_REFER: [
    { label: 'Tableau de bord',  route: '/federation/dashboard',    icon: 'dashboard' },
    { label: 'Mes matchs',       route: '/federation/mes-matchs',    icon: 'sports_soccer' },
    { label: 'Feuilles de match',route: '/federation/feuilles',      icon: 'description' },
  ],
  FED_DELEG: [
    { label: 'Tableau de bord',  route: '/federation/dashboard',    icon: 'dashboard' },
    { label: 'Mes matchs',       route: '/federation/mes-matchs',    icon: 'sports_soccer' },
    { label: 'Rapports',         route: '/federation/rapports',      icon: 'summarize' },
  ],
  CLUB_MANAGER: [
    { label: 'Tableau de bord',  route: '/club/dashboard',           icon: 'dashboard' },
    { label: 'Mes équipes',      route: '/club/equipes',             icon: 'group' },
    { label: 'Calendrier',       route: '/club/calendrier',          icon: 'calendar_month' },
    { label: 'Résultats',        route: '/club/resultats',           icon: 'scoreboard' },
  ],
  CLUB_READONLY: [
    { label: 'Tableau de bord',  route: '/club/dashboard',           icon: 'dashboard' },
    { label: 'Calendrier',       route: '/club/calendrier',          icon: 'calendar_month' },
    { label: 'Résultats',        route: '/club/resultats',           icon: 'scoreboard' },
  ],
  // Self-service club roles (2026-08-27) — only /club/dashboard exists behind their real
  // permission (competitions:read) today; not pointing at /club/equipes|calendrier|resultats
  // like CLUB_MANAGER/CLUB_READONLY do, since those aren't part of this rollout's grant.
  CLUB_DELEG: [
    { label: 'Tableau de bord',  route: '/club/dashboard',           icon: 'dashboard' },
  ],
  CLUB_REFE: [
    { label: 'Tableau de bord',  route: '/club/dashboard',           icon: 'dashboard' },
  ],
  STAFF: [
    { label: 'Tableau de bord',  route: '/club/dashboard',           icon: 'dashboard' },
  ],
};

@Component({
  selector: 'app-sidebar',
  standalone: false,
  templateUrl: './sidebar.component.html',
  styleUrls: ['./sidebar.component.scss'],
})
export class SidebarComponent implements OnDestroy {
  // Tablet (768-1023px): start on the icon rail so the 256px menu does not eat half the screen.
  isOpen = signal(typeof window === 'undefined' || window.innerWidth < 768 || window.innerWidth >= 1024);
  mobileOpen = signal(false);
  currentUrl = signal<string>('');

  private destroy$ = new Subject<void>();
  private sports = signal<SportOption[]>([]);
  // Keyed by sport_id — only the COMPETITION_TYPE codes that sport actually has competitions
  // under (real usage), not every type that exists. Never restricts creation, only the browse
  // menu: see usedCompetitionTypesBySport on the backend.
  private typesBySport = signal<Map<number, CompetitionTypeOption[]>>(new Map());
  // Explicit open/closed overrides from clicking a chevron — only present once the user has
  // actually toggled that item, so untouched items keep following the index-0-only default.
  private manualExpand = signal<Map<string, boolean>>(new Map());

  // Flat multi-role model — no primary/secondary role, so the nav is the UNION of every
  // matched role's items (deduped by route), not a single role's list. A sport-scoped route
  // (today only "Compétitions") fans out into one item per sport once they've loaded, each
  // carrying one child per competition type (Championnat, Coupe, ...) — navigating straight to
  // the sport+type URL, never through an in-page filter. Falls back to the single generic route
  // until the sports/types have loaded, or if the federation has none.
  navItems = computed<NavItem[]>(() => {
    const roles = this.authService.getRoles();
    const sports = this.sports();
    const typesBySport = this.typesBySport();
    const seenRoutes = new Set<string>();
    const items: NavItem[] = [];
    for (const role of roles) {
      for (const item of NAV_BY_ROLE[role] ?? []) {
        if (SPORT_SCOPED_ROUTES.has(item.route) && sports.length) {
          for (const s of sports) {
            const route = `${item.route}/sport/${s.sport_id}`;
            if (seenRoutes.has(route)) continue;
            seenRoutes.add(route);
            const types = typesBySport.get(s.sport_id) ?? [];
            const children = types.map(t => ({
              label: t.codification_label,
              route: `${route}/type/${t.codification_code}`,
              icon: item.icon,
            }));
            items.push({ label: s.sport_name, route, icon: item.icon, children: children.length ? children : undefined });
          }
          continue;
        }
        if (!seenRoutes.has(item.route)) {
          seenRoutes.add(item.route);
          items.push(item);
        }
      }
    }
    return items.length ? items : NAV_BY_ROLE['FED_ADMIN'];
  });

  platformLabel = computed<string>(() => {
    const roles = this.authService.getRoles();
    if (roles.includes('FED_REFER')) return 'Arbitre';
    if (roles.includes('FED_DELEG')) return 'Délégué';
    if (this.authService.isClub()) return 'Club';
    return 'Fédération';
  });

  constructor(
    private router: Router,
    private authService: AuthService,
    private permService: PermissionService,
    private api: ApiService,
  ) {
    this.router.events.pipe(
      filter(e => e instanceof NavigationEnd),
      takeUntil(this.destroy$)
    ).subscribe((e: any) => this.currentUrl.set(e.urlAfterRedirects));

    const roles = this.authService.getRoles();
    const hasSportScopedRoute = roles.some(r => (NAV_BY_ROLE[r] ?? []).some(i => SPORT_SCOPED_ROUTES.has(i.route)));
    if (hasSportScopedRoute) {
      this.api.get('/api/competitions/ref/sports')
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (r: any) => {
            const list: SportOption[] = r.data ?? [];
            this.sports.set(list);
            // One call per sport (there are only ever a handful) — each sport's menu reflects
            // only the types it actually has competitions under.
            for (const s of list) {
              this.api.get(`/api/competitions/ref/codifications?group=COMPETITION_TYPE&sport=${s.sport_id}`)
                .pipe(takeUntil(this.destroy$))
                .subscribe({
                  next: (tr: any) => this.typesBySport.update(map => new Map(map).set(s.sport_id, tr.data ?? [])),
                });
            }
          },
        });
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  toggleSidebar(): void { this.isOpen.update(v => !v); }
  toggleMobile(): void {
    this.mobileOpen.update(v => !v);
    // The phone drawer always shows labels, never the icon rail.
    if (this.mobileOpen() && !this.isOpen()) this.isOpen.set(true);
  }
  closeMobile(): void { this.mobileOpen.set(false); }

  isActive(route: string): boolean {
    return this.currentUrl().startsWith(route);
  }

  /** Only the first item is open by default; the rest start collapsed. Any item still opens
   *  automatically if the current URL is one of its children, and a manual click always wins
   *  over both defaults. */
  isExpanded(item: NavItem, index: number): boolean {
    if (!item.children?.length) return false;
    const manual = this.manualExpand().get(item.route);
    if (manual !== undefined) return manual;
    return index === 0 || item.children.some(c => this.isActive(c.route));
  }

  toggleExpand(item: NavItem, index: number, event: Event): void {
    event.stopPropagation();
    const next = !this.isExpanded(item, index);
    this.manualExpand.update(map => new Map(map).set(item.route, next));
  }

  navigate(item: NavItem, index = -1, event?: Event): void {
    if (item.children?.length) { this.toggleExpand(item, index, event ?? new Event('click')); return; }
    this.closeMobile();
    this.router.navigate([item.route]);
  }

  /** Collapsed icon-only rail has no room for a submenu — a sport icon goes straight to its
   *  unscoped list regardless of children. */
  navigateDirect(item: NavItem): void {
    this.closeMobile();
    this.router.navigate([item.route]);
  }
}
